import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import * as sessionsApi from '../api/sessions';

/**
 * Global "preparing video & audio" indicator for the portal header.
 *
 * After a split — or a video/audio upload — the worker prepares scores,
 * trimmed media, video frames and audio labels asynchronously. This polls
 * the tenant-wide active-insights endpoint and surfaces per-session
 * progress in the app bar while anything is in flight, so the operator sees
 * it regardless of which screen they're on. Renders nothing when idle.
 *
 * Real-estate: one session prepping shows a compact inline bar; several at
 * once collapse to a tiny "N preparing" chip that opens a popover listing
 * every in-flight session with its own mini bar — so concurrent uploads
 * never overflow the header.
 */

const POLL_MS = 4000;

const NOUN: Record<string, string> = {
  thumbnails: 'images',
  marker_labels: 'labels',
};

function who(a: sessionsApi.ActiveInsight): string {
  return [a.participant, a.session].filter(Boolean).join(' · ');
}

function stageText(a: sessionsApi.ActiveInsight): string {
  const noun = NOUN[a.kind];
  const hasCount = a.total != null && a.total > 0;
  return hasCount && noun ? `${a.label} ${a.done}/${a.total}` : `${a.label}…`;
}

function progressValue(a: sessionsApi.ActiveInsight): number | undefined {
  const hasCount = a.total != null && a.total > 0;
  return hasCount ? (a.done! / a.total!) * 100 : undefined;
}

/** One session's who + stage + mini bar. Reused inline and in the popover. */
function PrepRow({
  a,
  sx,
}: {
  a: sessionsApi.ActiveInsight;
  sx?: object;
}) {
  const value = progressValue(a);
  return (
    <Box sx={sx}>
      <Typography variant="caption" noWrap sx={{ fontWeight: 600, opacity: 0.95 }}>
        {who(a) || 'Preparing session'}
      </Typography>
      <Typography
        variant="caption"
        noWrap
        component="div"
        sx={{ fontSize: '0.62rem', opacity: 0.85, lineHeight: 1.2 }}
      >
        {stageText(a)}
      </Typography>
      <LinearProgress
        color="inherit"
        variant={value === undefined ? 'indeterminate' : 'determinate'}
        value={value}
        sx={{ height: 4, borderRadius: 1, mt: 0.25, opacity: 0.8 }}
      />
    </Box>
  );
}

export function HeaderMediaPrep({ tenantId }: { tenantId: string }) {
  const [info, setInfo] = useState<sessionsApi.ActiveInsights | null>(null);
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const poll = async () => {
      try {
        const r = await sessionsApi.getActiveInsights(tenantId);
        if (!cancelled) setInfo(r);
      } catch {
        /* transient — keep polling */
      }
      if (!cancelled) timer = setTimeout(poll, POLL_MS);
    };
    void poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [tenantId]);

  const items = info?.items ?? [];
  if (!info?.processing || items.length === 0) return null;

  // Single session — compact inline bar, no popover needed.
  if (items.length === 1) {
    return <PrepRow a={items[0]} sx={{ mr: 1.5, minWidth: 190, maxWidth: 280 }} />;
  }

  // Several at once — collapse to a chip that opens a per-session popover.
  return (
    <>
      <ButtonBase
        ref={anchorRef}
        onClick={() => setOpen(true)}
        sx={{
          mr: 1.5,
          px: 1,
          py: 0.5,
          borderRadius: 1.5,
          gap: 0.75,
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <CircularProgress size={14} color="inherit" />
        <Typography variant="caption" noWrap sx={{ fontWeight: 600 }}>
          {items.length} preparing
        </Typography>
        <ExpandMoreIcon sx={{ fontSize: 16, opacity: 0.7 }} />
      </ButtonBase>
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { p: 1.5, width: 300 } } }}
      >
        <Typography
          variant="overline"
          sx={{ display: 'block', opacity: 0.7, mb: 0.5 }}
        >
          Preparing media ({items.length})
        </Typography>
        <Stack spacing={1.25}>
          {items.map((a, i) => (
            <PrepRow key={`${a.participant}-${a.session}-${i}`} a={a} />
          ))}
        </Stack>
      </Popover>
    </>
  );
}
